import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Beaker, Trophy, TrendingUp, TrendingDown, Clock, ShieldAlert,
  ThumbsUp, ThumbsDown, Sparkles, AlertTriangle, Play, Loader as LoaderIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  PageShell, PageHeader, Card, Section, Pill, PrimaryButton, GhostButton,
  Loader, Alert, NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";

const RISK_META = {
  low:      { label: "Faible",    color: "#065F46", tone: "green" },
  medium:   { label: "Modéré",    color: "#B45309", tone: "amber" },
  high:     { label: "Élevé",     color: "#B91C1C", tone: "red"   },
  critical: { label: "Critique",  color: "#7F1D1D", tone: "red"   },
};

/**
 * Simulator — Décision What-If Engine.
 *
 * Compare 2-4 scénarios déterministes et affiche pour chacun : gain, risque,
 * dimensions impactées, avantages, inconvénients, risques, opportunités.
 * Le verdict du Directeur explique pourquoi un scénario l'emporte.
 */
export default function Simulator() {
  const [presets, setPresets] = useState(null);
  const [context, setContext] = useState("hot_prospect_timing");
  const [scenarios, setScenarios] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/simulator/presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  const applyPreset = useCallback((key) => {
    if (!presets?.[key]) return;
    setContext(key);
    setScenarios(presets[key].scenarios);
    setResult(null);
  }, [presets]);

  useEffect(() => {
    if (presets && !scenarios) applyPreset("hot_prospect_timing");
  }, [presets, scenarios, applyPreset]);

  const runSimulation = useCallback(async () => {
    if (!scenarios) return;
    setLoading(true);
    try {
      const r = await api.post("/simulator/simulate", { context, scenarios });
      setResult(r.data);
      setError(null);
      altioraToast.success("Simulation exécutée");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      altioraToast.error("Simulation impossible");
    } finally {
      setLoading(false);
    }
  }, [context, scenarios]);

  return (
    <PageShell testid="simulator-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="ALTIORA · Simulateur de Décision"
          title="Testez vos décisions avant d'agir"
          subtitle="Comparez plusieurs scénarios en un instant — impact sur le Score de Trajectoire, revenu attendu, temps, risque et confiance. Chaque projection est déterministe et reproductible."
          testid="simulator-header"
        />

        {/* Preset selector */}
        {presets && (
          <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="simulator-presets">
            <span className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: SLATE }}>
              Scénarios prêts à l'emploi
            </span>
            {Object.entries(presets).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className="text-[12px] px-3 py-1.5 rounded-full transition-all"
                style={{
                  background: context === key ? NAVY : "transparent",
                  color: context === key ? IVORY : NAVY,
                  border: `1px solid ${context === key ? NAVY : CHAMPAGNE}55`,
                }}
                data-testid={`simulator-preset-${key}`}
              >
                {meta.label}
              </button>
            ))}
          </div>
        )}

        {/* Scenarios input preview */}
        {scenarios && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="simulator-inputs">
            {scenarios.map((s) => (
              <div key={s.key} className="rounded-xl p-4 bg-white"
                   style={{ border: `1px solid ${CHAMPAGNE}33` }}>
                <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: CHAMPAGNE }}>
                  Scénario {s.key}
                </div>
                <div className="mt-1 font-serif text-lg tracking-tight" style={{ color: NAVY }}>
                  {s.label}
                </div>
                <div className="mt-2 text-[11px] font-mono" style={{ color: SLATE }}>
                  {Object.entries(s.params).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <PrimaryButton onClick={runSimulation} disabled={loading || !scenarios} testid="simulator-run">
            {loading ? (
              <><LoaderIcon className="h-3.5 w-3.5 mr-1.5 inline animate-spin" /> Simulation en cours…</>
            ) : (
              <><Play className="h-3.5 w-3.5 mr-1.5 inline" /> Lancer la simulation</>
            )}
          </PrimaryButton>
        </div>

        {error && (
          <div className="mt-6">
            <Alert variant="danger" title="Erreur de simulation" testid="simulator-error">{error}</Alert>
          </div>
        )}

        {/* Result */}
        {result && <ResultView result={result} />}
      </div>
    </PageShell>
  );
}


function ResultView({ result }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-10"
      data-testid="simulator-result"
    >
      {/* Director verdict */}
      <div
        className="rounded-2xl p-6 md:p-7"
        style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #061528 100%)`,
          boxShadow: "0 8px 32px -12px rgba(11,37,69,0.35)",
        }}
        data-testid="simulator-verdict"
      >
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="h-4 w-4" style={{ color: GOLD }} strokeWidth={2} />
          <span className="text-[10px] font-mono uppercase tracking-[0.32em]" style={{ color: GOLD }}>
            Verdict du Directeur commercial
          </span>
        </div>
        <p className="font-serif text-[18px] md:text-[20px] leading-relaxed"
           style={{ color: IVORY }}
           data-testid="simulator-verdict-body">
          {result.director_verdict}
        </p>
        <p className="mt-3 text-[13px]" style={{ color: `${IVORY}99` }}
           data-testid="simulator-summary">
          {result.comparative_summary}
        </p>
      </div>

      {/* Scenarios grid */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="simulator-scenarios">
        {result.scenarios.map((s) => (
          <ScenarioCard key={s.key} scenario={s} winner={s.key === result.recommended_key} />
        ))}
      </div>
    </motion.div>
  );
}


function ScenarioCard({ scenario: s, winner }) {
  const risk = RISK_META[s.risk_level] || RISK_META.medium;
  const trajArrow = s.trajectory_delta > 0 ? TrendingUp : TrendingDown;
  const TrajArrow = trajArrow;
  const trajColor = s.trajectory_delta > 0 ? "#065F46" : s.trajectory_delta < 0 ? "#B91C1C" : SLATE;
  return (
    <motion.article
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
      className="rounded-xl bg-white p-5 flex flex-col gap-3"
      style={{
        border: winner ? `2px solid ${GOLD}` : `1px solid ${CHAMPAGNE}33`,
        boxShadow: winner ? "0 8px 24px -8px rgba(245,214,135,0.35)" : "0 2px 8px -3px rgba(11,37,69,0.06)",
      }}
      data-testid={`scenario-card-${s.key}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: CHAMPAGNE }}>
            Scénario {s.key}
          </div>
          <h3 className="mt-1 font-serif text-lg tracking-tight" style={{ color: NAVY }}
              data-testid={`scenario-label-${s.key}`}>
            {s.label}
          </h3>
        </div>
        {winner && (
          <Pill tone="amber" testid={`scenario-winner-${s.key}`}>
            <Trophy className="h-3 w-3 mr-1 inline" /> Recommandé
          </Pill>
        )}
      </div>

      {/* Deltas grid */}
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <MiniStat icon={TrajArrow} color={trajColor} label="Trajectoire"
                  value={`${s.trajectory_delta > 0 ? "+" : ""}${s.trajectory_delta} pts`}
                  testid={`scenario-traj-${s.key}`} />
        <MiniStat icon={Sparkles} color={GOLD} label="Revenu"
                  value={`${s.revenue_delta_cad > 0 ? "+" : ""}${Math.round(s.revenue_delta_cad).toLocaleString("fr-CA")} $`}
                  testid={`scenario-rev-${s.key}`} />
        <MiniStat icon={Clock} color={CHAMPAGNE} label="Temps"
                  value={`${s.time_hours} h`}
                  testid={`scenario-time-${s.key}`} />
        <MiniStat icon={ShieldAlert} color={risk.color} label="Risque"
                  value={risk.label}
                  testid={`scenario-risk-${s.key}`} />
      </div>

      <div className="flex items-center gap-2 text-[11px]" style={{ color: SLATE }}>
        <span>Confiance</span>
        <strong style={{ color: NAVY }}>{s.confidence}/100</strong>
        <span className="mx-1">·</span>
        <span>Score net</span>
        <strong style={{ color: NAVY }}>{s.net_score > 0 ? "+" : ""}{s.net_score}</strong>
      </div>

      {/* Advantages / Disadvantages / Risks / Opportunities */}
      <ProConList label="Avantages" items={s.advantages} icon={ThumbsUp}
                  color="#065F46" bg={GREEN_LEAF} testid={`scenario-adv-${s.key}`} />
      <ProConList label="Inconvénients" items={s.disadvantages} icon={ThumbsDown}
                  color="#7F1D1D" bg={RED_LEAF} testid={`scenario-dis-${s.key}`} />
      <ProConList label="Risques" items={s.risks} icon={AlertTriangle}
                  color="#B45309" bg="#FDE68A" testid={`scenario-risks-${s.key}`} />
      <ProConList label="Opportunités" items={s.opportunities} icon={Sparkles}
                  color="#7A5B14" bg={GOLD} testid={`scenario-opps-${s.key}`} />

      {/* Dimension deltas */}
      {Object.keys(s.dimension_deltas || {}).length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1" style={{ color: CHAMPAGNE }}>
            Impact sur le Score de Trajectoire
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid={`scenario-dims-${s.key}`}>
            {Object.entries(s.dimension_deltas).map(([k, v]) => (
              <span key={k}
                    className="text-[10px] font-mono uppercase tracking-[0.20em] px-1.5 py-0.5 rounded"
                    style={{
                      background: v > 0 ? `${GREEN_LEAF}22` : v < 0 ? `${RED_LEAF}22` : `${SLATE}22`,
                      color: v > 0 ? "#065F46" : v < 0 ? "#7F1D1D" : NAVY,
                    }}>
                {k} {v > 0 ? "+" : ""}{v}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.article>
  );
}


function MiniStat({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="rounded-md px-2.5 py-1.5"
         style={{ background: `${color}12`, border: `1px solid ${color}22` }}
         data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em]"
           style={{ color: SLATE }}>
        <Icon className="h-3 w-3" strokeWidth={2} /> {label}
      </div>
      <div className="text-sm font-serif tabular-nums" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}


function ProConList({ label, items, icon: Icon, color, bg, testid }) {
  if (!items || !items.length) return null;
  return (
    <div className="rounded-md p-2.5 border-l-2"
         style={{ background: `${bg}18`, borderColor: color }}
         data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
           style={{ color }}>
        <Icon className="h-3 w-3" strokeWidth={2} /> {label}
      </div>
      <ul className="text-[12px] leading-relaxed space-y-0.5" style={{ color: NAVY }}>
        {items.map((it, i) => (<li key={i}>· {it}</li>))}
      </ul>
    </div>
  );
}
